import { some, every, keyBy, sumBy } from 'lodash';
import { defaultMemoize, createSelector } from 'reselect';
import buildValidators from '../../../utils/validation/buildValidators';
import { getSelectedAdmissionItem, getRegistrationTypeId, getSelectedQuantityItems }
  from '../../../utils/eventRegistration';

const simplify = defaultMemoize(rule => ({
  min: rule.minQuantityAllowed || 0,
  max: rule.maxQuantityAllowed || Infinity
}));

const ruleHasMinMax = createSelector(
  simplify,
  minMax => minMax.min > 0 || minMax.max < Infinity
);

const quantityItemAdvancedRuleIsValid = (rule, selectedQuantityItems) => {
  const quantityItemsRuleApplied = selectedQuantityItems
    .filter(quantityItem => rule.quantityItem.includes(quantityItem.productId));
  const totalQuantitySelected = sumBy(quantityItemsRuleApplied, 'quantity');
  const minMax = simplify(rule);
  return totalQuantitySelected >= minMax.min && totalQuantitySelected <= minMax.max;
};

const ruleApplies = (rule, registrationTypeId, admissionItemId) =>
  some(rule.contactType, ruleRegistrationTypeId => registrationTypeId === ruleRegistrationTypeId) &&
  some(rule.admissionItem, ruleAdmissionItemId => admissionItemId === ruleAdmissionItemId);

export const speculativelyValidateQuantityItemAdvancedRules = (
  rules,
  registrationTypeId,
  admissionItem,
  selectedQuantityItems) => {
  const applicableRules = rules.filter(rule => ruleApplies(rule, registrationTypeId, admissionItem.id));

  return every(applicableRules, rule => quantityItemAdvancedRuleIsValid(rule, selectedQuantityItems));
};

const ruleHasAssociatedQuantityItems = rule => (rule.quantityItem && rule.quantityItem.length > 0);

const ruleAppliesToRegistrant = (rule, eventRegistration) => {
  if (!ruleHasAssociatedQuantityItems(rule) || !ruleHasMinMax(rule)) {
    return false;
  }

  const admissionItem = getSelectedAdmissionItem(eventRegistration);
  if (!admissionItem) {
    return false;
  }

  const registrationTypeId = getRegistrationTypeId(eventRegistration);

  return ruleApplies(rule, registrationTypeId, admissionItem.productId);
};

const quantityItemAdvancedRuleIsValidForRegistrant = (rule, eventRegistration) => {
  const selectedQuantityItems = getSelectedQuantityItems(eventRegistration);
  return quantityItemAdvancedRuleIsValid(rule, selectedQuantityItems);
};

const getRuleWarningMessage = (translate, rule) => translate(rule.warningMessage);

const builder = defaultMemoize(buildValidators({
  objectValidatesRegistrant: ruleAppliesToRegistrant,
  registrantIsValid: quantityItemAdvancedRuleIsValidForRegistrant,
  getValidationMessage: getRuleWarningMessage
}));

export default (args) => {
  const { advancedQuantityItemRules } = args;

  const rulesById = keyBy(advancedQuantityItemRules, 'id');

  return builder({
    ...args,
    validationObjects: rulesById
  });
};
